import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  enhancedChatForUserBirthChart,
  fetchEnhancedChatHistory,
  enhancedChatForRelationship,
  fetchRelationshipEnhancedChatHistory,
  fetchHoroscopeChatHistory,
  generateCustomHoroscope
} from '../../Utilities/api';
import useEntitlementsStore from '../../Utilities/entitlementsStore';
import useStore from '../../Utilities/store';
import { useAuth } from '../../context/AuthContext';
import InsufficientCreditsModal from '../entitlements/InsufficientCreditsModal';
import { trackChatMessageSent, trackCreditWallHit } from '../../Utilities/analytics';
import StreamingTextReveal from './StreamingTextReveal';
import './GravityChatPanel.css';

const HISTORY_CONFIG = {
  birthchart: {
    fetchHistory: (id, limit) => fetchEnhancedChatHistory(id, limit)
  },
  analysis: {
    fetchHistory: (id, limit) => fetchEnhancedChatHistory(id, limit)
  },
  relationship: {
    fetchHistory: (id, limit) => fetchRelationshipEnhancedChatHistory(id, limit)
  },
  horoscope: {
    fetchHistory: (id, limit) => fetchHoroscopeChatHistory(id, limit)
  }
};

const MAX_SELECTIONS = 3;

const signToCode = {
  Aries: 'Ar', Taurus: 'Ta', Gemini: 'Ge', Cancer: 'Ca',
  Leo: 'Le', Virgo: 'Vi', Libra: 'Li', Scorpio: 'Sc',
  Sagittarius: 'Sa', Capricorn: 'Cp', Aquarius: 'Aq', Pisces: 'Pi'
};

const planetToCode = {
  Sun: 'Su', Moon: 'Mo', Mercury: 'Me', Venus: 'Ve', Mars: 'Ma',
  Jupiter: 'Ju', Saturn: 'Sa', Uranus: 'Ur', Neptune: 'Ne', Pluto: 'Pl',
  Ascendant: 'As', Midheaven: 'Mc', Node: 'No', 'North Node': 'No', Chiron: 'Ch'
};

const aspectToCode = {
  conjunction: 'Co', opposition: 'Op', trine: 'Tr',
  square: 'Sq', sextile: 'Sx', quincunx: 'Qu'
};

const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const findPlanetData = (planetName, planets) => {
  return planets.find(p => p.name === planetName) || null;
};

export const formatAspectData = (aspect, planet1Data, planet2Data) => {
  const aspectType = (aspect.aspectType || 'aspect').toLowerCase();
  const planet1Code = planetToCode[aspect.aspectedPlanet] || aspect.aspectedPlanet.substring(0, 2);
  const planet2Code = planetToCode[aspect.aspectingPlanet] || aspect.aspectingPlanet.substring(0, 2);
  const aspectCode = aspectToCode[aspectType] || aspectType.substring(0, 2);

  const planet1Sign = signToCode[planet1Data.sign] || planet1Data.sign.substring(0, 2);
  const planet2Sign = signToCode[planet2Data.sign] || planet2Data.sign.substring(0, 2);

  const planet1House = planet1Data.house ? String(planet1Data.house).padStart(2, '0') : '00';
  const planet2House = planet2Data.house ? String(planet2Data.house).padStart(2, '0') : '00';

  return {
    group: 'birthchart',
    type: 'aspect',
    code: `A-${planet1Code}s${planet1Sign}${planet1House}Ca${aspectCode}${planet2Code}s${planet2Sign}${planet2House}`,
    planet1: aspect.aspectedPlanet,
    planet1Sign: planet1Data.sign,
    planet1House: planet1Data.house || null,
    planet2: aspect.aspectingPlanet,
    planet2Sign: planet2Data.sign,
    planet2House: planet2Data.house || null,
    aspectType: aspectType,
    orb: aspect.orb,
    label: `${aspect.aspectedPlanet} ${aspectType} ${aspect.aspectingPlanet}`,
    description: `${aspect.aspectedPlanet} in ${planet1Data.sign}${planet1Data.house ? ` in ${getOrdinal(planet1Data.house)} house` : ''} ${aspectType} ${aspect.aspectingPlanet} in ${planet2Data.sign}${planet2Data.house ? ` in ${getOrdinal(planet2Data.house)} house` : ''}`
  };
};

export const formatPositionData = (planet) => {
  const planetCode = planetToCode[planet.name] || planet.name.substring(0, 2);
  const signCode = signToCode[planet.sign] || planet.sign.substring(0, 2);
  const houseNumber = planet.house ? String(planet.house).padStart(2, '0') : '00';

  return {
    group: 'birthchart',
    type: 'position',
    code: `Pp-${planetCode}${signCode}${houseNumber}`,
    planet: planet.name,
    sign: planet.sign,
    house: planet.house || null,
    isRetrograde: planet.is_retro === 'true',
    degree: planet.norm_degree,
    label: `${planet.name} in ${planet.sign}`,
    description: `${planet.name} in ${planet.sign}${planet.house ? ` in ${getOrdinal(planet.house)} house` : ''}`
  };
};

export const formatHouseData = (house) => {
  const houseNumber = house.house || house.number || house.houseNumber;
  const sign = house.sign || house.zodiacSign || house.signName || '';
  const degree = house.degree ?? house.norm_degree ?? house.longitude;
  return {
    group: 'birthchart',
    type: 'house',
    code: `H-${houseNumber || 'unknown'}-${sign || 'sign'}`,
    house: houseNumber || null,
    sign,
    degree,
    label: houseNumber ? `${getOrdinal(houseNumber)} House${sign ? ` in ${sign}` : ''}` : (house.label || 'House placement'),
    description: house.description || (houseNumber ? `${getOrdinal(houseNumber)} house${sign ? ` begins in ${sign}` : ''}` : 'House placement')
  };
};

const normalizePatternLabel = (key) => {
  return String(key || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, char => char.toUpperCase());
};

export const formatPatternData = (pattern, fallbackKey, index) => {
  const label = typeof pattern === 'string'
    ? pattern
    : pattern?.label || pattern?.name || pattern?.title || pattern?.pattern || normalizePatternLabel(fallbackKey);
  const description = typeof pattern === 'string'
    ? pattern
    : pattern?.description || pattern?.interpretation || pattern?.summary || label;
  return {
    group: 'birthchart',
    type: 'pattern',
    code: `PAT-${fallbackKey || 'pattern'}-${index}`,
    label,
    description,
    payload: pattern
  };
};

export const flattenPatterns = (patterns) => {
  if (!patterns) return [];
  if (Array.isArray(patterns)) {
    return patterns.map((pattern, index) => formatPatternData(pattern, 'pattern', index));
  }
  if (typeof patterns !== 'object') return [];

  return Object.entries(patterns).flatMap(([key, value]) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((pattern, index) => formatPatternData(pattern, key, index));
    }
    if (typeof value === 'object') {
      const nested = Object.entries(value)
        .filter(([, nestedValue]) => Array.isArray(nestedValue))
        .flatMap(([nestedKey, nestedValue]) => nestedValue.map((pattern, index) => (
          formatPatternData(pattern, `${key}-${nestedKey}`, index)
        )));
      if (nested.length > 0) return nested;
    }
    return [formatPatternData(value, key, 0)];
  });
};

export const formatTransitEvent = (transit) => ({
  type: transit.type,
  transitingPlanet: transit.transitingPlanet,
  exact: transit.exact,
  targetPlanet: transit.targetPlanet,
  aspect: transit.aspect,
  start: transit.start,
  end: transit.end,
  description: transit.description,
  transitingSign: transit.transitingSign,
  targetSign: transit.targetSign,
  transitingHouse: transit.transitingHouse,
  targetHouse: transit.targetHouse,
  moonPhaseData: transit.moonPhaseData
});

const formatDateShort = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatTransitDateRange = (transit) => {
  const start = formatDateShort(transit.start);
  const end = formatDateShort(transit.end);
  const exact = formatDateShort(transit.exact);

  if (!start && !end) return exact || '';
  if (start && end && start === end) return start;
  if (start && end && exact && exact !== start && exact !== end) {
    return `${start} - ${end} (exact: ${exact})`;
  }
  if (start && end) return `${start} - ${end}`;
  return start || end || '';
};

const getFilterOptions = (contentType) => {
  if (contentType === 'birthchart' || contentType === 'analysis') {
    return [
      { value: 'all', label: 'All' },
      { value: 'positions', label: 'Positions' },
      { value: 'houses', label: 'Houses' },
      { value: 'aspects', label: 'Aspects' },
      { value: 'patterns', label: 'Patterns' }
    ];
  }
  if (contentType === 'relationship') {
    return [
      { value: 'all', label: 'All' },
      { value: 'synastry', label: 'Synastry' },
      { value: 'composite', label: 'Composite' },
      { value: 'placements', label: 'Placements' }
    ];
  }
  if (contentType === 'horoscope') {
    return [
      { value: 'all', label: 'All' },
      { value: 'transit-to-natal', label: 'Natal' },
      { value: 'transit-to-transit', label: 'World' }
    ];
  }
  return [];
};

const getCategoryBadgeInfo = (contentType, element) => {
  if (contentType === 'birthchart' || contentType === 'analysis') {
    if (element.type === 'position') {
      return { label: 'Position', colorClass: 'badge--position' };
    }
    if (element.type === 'house') {
      return { label: 'House', colorClass: 'badge--house' };
    }
    if (element.type === 'pattern') {
      return { label: 'Pattern', colorClass: 'badge--pattern' };
    }
    return { label: 'Aspect', colorClass: 'badge--aspect' };
  }
  if (contentType === 'relationship') {
    const source = (element.payload?.source || element.meta || '').toLowerCase();
    if (source.includes('composite')) return { label: 'Composite', colorClass: 'badge--composite' };
    if (source.includes('houseplacement')) return { label: 'Placements', colorClass: 'badge--placements' };
    return { label: 'Synastry', colorClass: 'badge--synastry' };
  }
  if (contentType === 'horoscope') {
    const transitType = element.payload?.type || '';
    if (transitType === 'transit-to-transit') return { label: 'World', colorClass: 'badge--world' };
    return { label: 'Natal', colorClass: 'badge--natal' };
  }
  return { label: '', colorClass: '' };
};

const titleCase = (value) => String(value || '')
  .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());

const getContextTableCells = (contentType, element) => {
  const payload = element.payload || element;
  if ((contentType === 'birthchart' || contentType === 'analysis') && payload.type === 'position') {
    const degree = Number(payload.degree);
    return {
      primary: payload.planet,
      detail: [Number.isFinite(degree) ? `${degree.toFixed(1)}°` : null, payload.sign]
        .filter(Boolean)
        .join(' '),
      metric: payload.house ? `House ${payload.house}` : '—',
    };
  }
  if ((contentType === 'birthchart' || contentType === 'analysis') && payload.type === 'aspect') {
    const orb = Number(payload.orb);
    return {
      primary: payload.planet1,
      detail: `${titleCase(payload.aspectType)} ${payload.planet2}`,
      metric: Number.isFinite(orb) ? `${orb.toFixed(1)}° orb` : '—',
    };
  }
  if ((contentType === 'birthchart' || contentType === 'analysis') && payload.type === 'house') {
    return {
      primary: payload.house ? `House ${payload.house}` : 'House',
      detail: payload.sign || payload.label,
      metric: 'Cusp',
    };
  }
  return {
    primary: element.label || element.description || element.code,
    detail: element.meta || '',
    metric: getCategoryBadgeInfo(contentType, element).label,
  };
};

const makeMessageId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function GravityChatPanel({
  isOpen,
  onClose,
  contentType,
  contentId,
  contextLabel,
  placeholderText,
  suggestedQuestions,
  birthChart,
  relationshipScoredItems,
  transitWindows = [],
  horoscopePeriod,
  disableHistory = false,
  variant = 'overlay',
  autoFocus = variant === 'overlay',
  defaultContextOpen = false,
  contextPlacement = 'inline',
  contextContainer,
  externalElements,
  syncExternalElements = false,
  externalToggle,
  externalMessage,
  externalInspect,
  externalHover,
  onElementHover,
  onElementInspect,
  onSelectionChange,
  onLoadingChange,
  getActiveDate,
  includeHoroscopeReading = true,
  preserveSelectionOnSend = false,
  selectionLimit = MAX_SELECTIONS
}) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallGate, setPaywallGate] = useState(null);
  const [selectedElements, setSelectedElements] = useState([]);
  const [selectionError, setSelectionError] = useState(null);
  const [activePeriod, setActivePeriod] = useState('weekly');
  const [transitTypeFilter, setTransitTypeFilter] = useState('all');
  const [relationshipFilter, setRelationshipFilter] = useState('all');
  const [birthchartFilter, setBirthchartFilter] = useState(
    contentType === 'birthchart' || contentType === 'analysis' ? 'positions' : 'all'
  );
  const [overlayOpen, setOverlayOpen] = useState(defaultContextOpen);
  const [overlaySearch, setOverlaySearch] = useState('');
  const [inspectorFocus, setInspectorFocus] = useState(null);
  const messagesEndRef = useRef(null);
  const panelRef = useRef(null);
  const textareaRef = useRef(null);
  const hasLoadedRef = useRef(null);
  const overlayRef = useRef(null);
  const externalToggleRef = useRef(null);
  const externalInspectRef = useRef(null);
  const externalElementsRef = useRef(null);
  const externalMessageRef = useRef(null);
  const selectedElementsRef = useRef([]);
  const isOverlayVariant = variant === 'overlay';
  const persistentContext = contextPlacement === 'external';

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const navigate = useNavigate();
  const { stelliumUser } = useAuth();
  const hasEnoughCredits = useEntitlementsStore(state => state.hasEnoughCredits);
  const isPlus = useEntitlementsStore(state =>
    (state.plan === 'PLUS' || state.plan === 'PREMIUM') && state.isSubscriptionActive
  );
  const credits = useEntitlementsStore(state => state.credits);
  const isSimple = useEntitlementsStore(state => state.pricingModel === 'simple');
  const chatQuestionsRemaining = useEntitlementsStore(state => state.getChatQuestionsRemaining());
  const fetchEntitlements = useEntitlementsStore(state => state.fetchEntitlements);
  const applyOptimisticCreditSpend = useEntitlementsStore(state => state.applyOptimisticCreditSpend);
  const restoreCredits = useEntitlementsStore(state => state.restoreCredits);
  const storedPlanets = useStore(state => state.userPlanets);
  const storedAspects = useStore(state => state.userAspects);
  const storedHouses = useStore(state => state.userHouses);
  const storedPatterns = useStore(state => state.userPatterns);

  const config = HISTORY_CONFIG[contentType];
  const planets = useMemo(
    () => birthChart?.planets || storedPlanets || [],
    [birthChart?.planets, storedPlanets]
  );
  const aspects = useMemo(
    () => birthChart?.aspects || storedAspects || [],
    [birthChart?.aspects, storedAspects]
  );
  const houses = useMemo(
    () => birthChart?.houses || storedHouses || [],
    [birthChart?.houses, storedHouses]
  );
  const patterns = useMemo(
    () => birthChart?.patterns || storedPatterns || {},
    [birthChart?.patterns, storedPatterns]
  );

  const resolvedPeriod = useMemo(() => {
    if (horoscopePeriod === 'today') return 'daily';
    if (horoscopePeriod === 'week') return 'weekly';
    if (horoscopePeriod === 'month') return 'monthly';
    return 'weekly';
  }, [horoscopePeriod]);

  // Load chat history when panel opens
  useEffect(() => {
    if (!isOpen || !contentId || !config) return;
    if (disableHistory) return; // celebrity Gravity Chat: ephemeral, don't load prior history
    if (hasLoadedRef.current === contentId) return;

    const loadHistory = async () => {
      setLoadingHistory(true);
      try {
        const history = await config.fetchHistory(contentId, 50);
        const historyArray = Array.isArray(history)
          ? history
          : Array.isArray(history?.chatHistory)
            ? history.chatHistory
            : [];
        if (historyArray.length > 0) {
          const mapped = historyArray.map((msg, idx) => ({
            id: msg.id || msg._id || `${contentId}-history-${idx}`,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp,
            selectedElements: msg.metadata?.selectedElements || msg.metadata?.selectedAspects || null
          }));
          setMessages(prev => (prev.length > 0 ? prev : mapped));
        }
      } catch (err) {
        console.error('Error loading chat history:', err);
      } finally {
        setLoadingHistory(false);
        hasLoadedRef.current = contentId;
      }
    };

    loadHistory();
  }, [isOpen, contentId, config, disableHistory]);

  // Reset state when contentId changes
  useEffect(() => {
    hasLoadedRef.current = null;
    setMessages([]);
    setSelectedElements([]);
    setSelectionError(null);
    setOverlayOpen(defaultContextOpen);
    setInspectorFocus(null);
  }, [contentId, contentType, defaultContextOpen]);

  useEffect(() => {
    setActivePeriod(resolvedPeriod);
  }, [resolvedPeriod]);

  // Clear search when overlay closes
  useEffect(() => {
    if (!overlayOpen) setOverlaySearch('');
  }, [overlayOpen]);

  useEffect(() => {
    if (!externalInspect?.element || externalInspectRef.current === externalInspect.nonce) return;
    externalInspectRef.current = externalInspect.nonce;
    setInspectorFocus(externalInspect.element);
    setOverlayOpen(true);
    setOverlaySearch('');
    if (contentType === 'birthchart' || contentType === 'analysis') {
      setBirthchartFilter('all');
    }
  }, [contentType, externalInspect]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const scrollToLatestMessage = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const markMessageRevealComplete = useCallback((messageId) => {
    setMessages(prev => prev.map(message => (
      message.id === messageId ? { ...message, animate: false } : message
    )));
  }, []);

  // Replay the streaming reveal animation on an existing answer
  const restreamMessage = useCallback((messageId) => {
    setMessages(prev => prev.map(message => (
      message.id === messageId ? { ...message, animate: true } : message
    )));
    setTimeout(scrollToLatestMessage, 0);
  }, [scrollToLatestMessage]);

  // Hide an answer from the current view (client-side only; not persisted)
  const hideMessage = useCallback((messageId) => {
    setMessages(prev => prev.filter(message => message.id !== messageId));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setMessages(prev => prev.map(message => (
        message.animate ? { ...message, animate: false } : message
      )));
    }
  }, [isOpen]);

  // Focus textarea when panel opens
  useEffect(() => {
    if (isOpen && autoFocus) {
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  }, [autoFocus, isOpen]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (overlayOpen) {
          setOverlayOpen(false);
        } else if (isOverlayVariant) {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isOverlayVariant, onClose, overlayOpen]);

  // Close overlay on outside click
  useEffect(() => {
    if (!overlayOpen || persistentContext) return;
    const handleClickOutside = (e) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target)) {
        setOverlayOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [overlayOpen, persistentContext]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen && isOverlayVariant) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('ask-stellium-panel-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('ask-stellium-panel-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('ask-stellium-panel-open');
    };
  }, [isOpen, isOverlayVariant]);

  const positionsData = useMemo(() => {
    return planets
      .filter(planet => !['South Node', 'Part of Fortune'].includes(planet.name))
      .map(planet => formatPositionData(planet));
  }, [planets]);

  const aspectsData = useMemo(() => {
    return aspects.map(aspect => {
      const planet1Data = findPlanetData(aspect.aspectedPlanet, planets);
      const planet2Data = findPlanetData(aspect.aspectingPlanet, planets);
      if (!planet1Data || !planet2Data) return null;
      return formatAspectData(aspect, planet1Data, planet2Data);
    }).filter(Boolean);
  }, [aspects, planets]);

  const housesData = useMemo(() => {
    return houses.map(formatHouseData).filter(item => item.house || item.sign || item.label);
  }, [houses]);

  const patternsData = useMemo(() => {
    return flattenPatterns(patterns).filter(item => item.label || item.description);
  }, [patterns]);

  const filteredBirthchartElements = useMemo(() => {
    const positions = positionsData.map(p => ({ ...p, key: p.code, payload: p }));
    const houseItems = housesData.map(h => ({ ...h, key: h.code, payload: h }));
    const aspectItems = aspectsData.map(a => ({ ...a, key: a.code, payload: a }));
    const patternItems = patternsData.map(p => ({ ...p, key: p.code, payload: p }));
    if (birthchartFilter === 'positions') return positions;
    if (birthchartFilter === 'houses') return houseItems;
    if (birthchartFilter === 'aspects') return aspectItems;
    if (birthchartFilter === 'patterns') return patternItems;
    return [...positions, ...houseItems, ...aspectItems, ...patternItems];
  }, [positionsData, housesData, aspectsData, patternsData, birthchartFilter]);

  const relationshipElements = useMemo(() => {
    if (!Array.isArray(relationshipScoredItems)) return [];
    return relationshipScoredItems.map((item, index) => ({
      group: 'relationship',
      type: item.type || item.source || 'relationship',
      key: `${item.code || item.id || item.source || 'rel'}-${index}`,
      label: item.description || item.reason || item.label || 'Relationship element',
      meta: item.source || item.category || '',
      payload: item
    }));
  }, [relationshipScoredItems]);

  const filteredRelationshipElements = useMemo(() => {
    let filtered;
    if (relationshipFilter === 'all') {
      filtered = [...relationshipElements];
    } else {
      filtered = relationshipElements.filter(element => {
        const source = (element.payload?.source || element.meta || '').toLowerCase();
        if (relationshipFilter === 'synastry') return source.includes('synastry');
        if (relationshipFilter === 'composite') return source.includes('composite');
        if (relationshipFilter === 'placements') return source.includes('houseplacement');
        return true;
      });
    }
    // Sort by badge type so section dividers can group cleanly
    const getOrder = (src) => {
      if (src.includes('composite')) return 1;
      if (src.includes('houseplacement')) return 2;
      return 0; // synastry aspects
    };
    filtered.sort((a, b) => {
      const aSource = (a.payload?.source || a.meta || '').toLowerCase();
      const bSource = (b.payload?.source || b.meta || '').toLowerCase();
      return getOrder(aSource) - getOrder(bSource);
    });
    return filtered;
  }, [relationshipElements, relationshipFilter]);

  const filteredTransits = useMemo(() => {
    if (!transitWindows || transitWindows.length === 0) return [];

    const now = new Date();
    let start;
    let end;

    if (activePeriod === 'daily') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    } else if (activePeriod === 'monthly') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    } else {
      const monday = new Date(now);
      monday.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
      monday.setHours(0, 0, 0, 0);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);
      start = monday;
      end = sunday;
    }

    return transitWindows
      .filter(transit => {
        const transitStart = new Date(transit.start);
        const transitEnd = new Date(transit.end);
        const inRange = transitStart <= end && transitEnd >= start;
        const matchesType = transitTypeFilter === 'all' || transit.type === transitTypeFilter;
        return inRange && matchesType;
      });
  }, [transitWindows, activePeriod, transitTypeFilter]);

  const transitElements = useMemo(() => {
    return filteredTransits.map((transit, index) => {
      const labelParts = [
        transit.transitingPlanet,
        transit.aspect,
        transit.targetPlanet
      ].filter(Boolean);
      return {
        group: 'horoscope',
        type: 'transit',
        key: transit.id || `${transit.transitingPlanet}-${transit.aspect}-${transit.targetPlanet}-${index}`,
        label: labelParts.join(' '),
        meta: transit.description || '',
        payload: formatTransitEvent(transit)
      };
    });
  }, [filteredTransits]);

  const selectableElements = useMemo(() => {
    if (contentType === 'birthchart' || contentType === 'analysis') {
      return filteredBirthchartElements;
    }
    if (contentType === 'relationship') {
      return filteredRelationshipElements;
    }
    if (contentType === 'horoscope') {
      return transitElements;
    }
    return [];
  }, [contentType, filteredBirthchartElements, filteredRelationshipElements, transitElements]);

  const filteredOverlayElements = useMemo(() => {
    let elements = selectableElements;
    if (inspectorFocus && (contentType === 'birthchart' || contentType === 'analysis')) {
      const focusKey = inspectorFocus.key || inspectorFocus.code;
      const focusPayload = inspectorFocus.payload || inspectorFocus;
      if (focusPayload.type === 'position') {
        const body = focusPayload.planet;
        elements = selectableElements.filter((element) => {
          const payload = element.payload || element;
          return (
            (payload.type === 'position' && payload.planet === body) ||
            (payload.type === 'aspect' && (payload.planet1 === body || payload.planet2 === body))
          );
        });
      } else if (focusPayload.type === 'aspect') {
        elements = selectableElements.filter((element) => (element.key || element.code) === focusKey);
      }
    }
    if (!overlaySearch.trim()) return elements;
    const query = overlaySearch.toLowerCase();
    return elements.filter(el =>
      (el.label || el.description || '').toLowerCase().includes(query)
    );
  }, [contentType, inspectorFocus, selectableElements, overlaySearch]);

  const hasContextData = useMemo(() => {
    if (contentType === 'birthchart' || contentType === 'analysis') {
      return positionsData.length > 0 || housesData.length > 0 || aspectsData.length > 0 || patternsData.length > 0;
    }
    if (contentType === 'relationship') {
      return relationshipElements.length > 0;
    }
    if (contentType === 'horoscope') {
      return transitWindows && transitWindows.length > 0;
    }
    return false;
  }, [contentType, positionsData, housesData, aspectsData, patternsData, relationshipElements, transitWindows]);

  const getActiveFilter = useCallback(() => {
    if (contentType === 'birthchart' || contentType === 'analysis') return birthchartFilter;
    if (contentType === 'relationship') return relationshipFilter;
    if (contentType === 'horoscope') return transitTypeFilter;
    return 'all';
  }, [contentType, birthchartFilter, relationshipFilter, transitTypeFilter]);

  const setActiveFilterCb = useCallback((value) => {
    if (contentType === 'birthchart' || contentType === 'analysis') setBirthchartFilter(value);
    else if (contentType === 'relationship') setRelationshipFilter(value);
    else if (contentType === 'horoscope') setTransitTypeFilter(value);
  }, [contentType]);

  const isSelected = useCallback((elementKey) => {
    return selectedElements.some(el => el.key === elementKey);
  }, [selectedElements]);

  const handleToggleElement = useCallback((element) => {
    setSelectionError(null);

    if (isSelected(element.key)) {
      setSelectedElements(prev => prev.filter(el => el.key !== element.key));
      return;
    }

    if (selectedElements.length >= selectionLimit) {
      setSelectionError(`Add up to ${selectionLimit} chart elements to focus your question.`);
      return;
    }

    setSelectedElements(prev => [...prev, element]);
  }, [isSelected, selectedElements.length, selectionLimit]);

  const clearSelections = useCallback(() => {
    setSelectedElements([]);
    setSelectionError(null);
  }, []);

  const removeSelection = useCallback((key) => {
    setSelectedElements(prev => prev.filter(el => el.key !== key));
    setSelectionError(null);
  }, []);

  // Stage bridge: elements pushed from outside (sky clicks, influence
  // pills) merge into the selection while surfacing the shared cap.
  useEffect(() => {
    if (externalElementsRef.current === externalElements) return;
    externalElementsRef.current = externalElements;
    if (syncExternalElements) {
      const next = (externalElements || []).filter((element) => element?.key).slice(0, selectionLimit);
      const previous = selectedElementsRef.current;
      const unchanged = previous.length === next.length && previous.every(
        (element, index) => element.key === next[index].key
      );
      if (!unchanged) setSelectedElements(next);
      setSelectionError(
        (externalElements?.length || 0) > selectionLimit
          ? `Add up to ${selectionLimit} chart elements to focus your question.`
          : null
      );
      return;
    }
    if (!externalElements?.length) return;
    const currentSelections = selectedElementsRef.current;
    const additions = externalElements.filter(
      (el) => el?.key && !currentSelections.some((selected) => selected.key === el.key)
    );
    if (!additions.length) return;

    const available = selectionLimit - currentSelections.length;
    if (available <= 0) {
      setSelectionError(`Add up to ${selectionLimit} chart elements to focus your question.`);
      return;
    }

    setSelectedElements([...currentSelections, ...additions.slice(0, available)]);
    setSelectionError(
      additions.length > available
        ? `Add up to ${selectionLimit} chart elements to focus your question.`
        : null
    );
  }, [externalElements, selectionLimit, syncExternalElements]);

  useEffect(() => {
    if (!externalToggle?.element || externalToggleRef.current === externalToggle.nonce) return;
    externalToggleRef.current = externalToggle.nonce;
    handleToggleElement(externalToggle.element);
  }, [externalToggle, handleToggleElement]);

  const inspectElement = useCallback((element) => {
    setInspectorFocus(element);
    onElementInspect?.(element);
  }, [onElementInspect]);

  const clearInspectorFocus = useCallback(() => {
    setInspectorFocus(null);
    onElementHover?.(null);
    onElementInspect?.(null);
  }, [onElementHover, onElementInspect]);

  const elementIsExternallyHovered = useCallback((element) => {
    if (!externalHover) return false;
    const payload = element.payload || element;
    const hoverPayload = externalHover.payload || externalHover;
    if (hoverPayload.type === 'position') {
      if (payload.type === 'position') return payload.planet === hoverPayload.planet;
      if (payload.type === 'aspect') {
        return payload.planet1 === hoverPayload.planet || payload.planet2 === hoverPayload.planet;
      }
    }
    return (element.key || element.code) === (externalHover.key || externalHover.code);
  }, [externalHover]);

  // let the host mirror the selection back onto the sky
  useEffect(() => {
    selectedElementsRef.current = selectedElements;
    onSelectionChange?.(selectedElements);
  }, [selectedElements, onSelectionChange]);

  const handleSendMessage = useCallback(async (messageOverride) => {
    const trimmedMessage = typeof messageOverride === 'string'
      ? messageOverride.trim()
      : inputMessage.trim();
    const hasSelection = selectedElements.length > 0;
    if ((!trimmedMessage && !hasSelection) || loading || !config) return;

    if (!contentId) {
      setError('Missing chat context. Please refresh and try again.');
      return;
    }

    if (!isPlus && !hasEnoughCredits(1)) {
      trackCreditWallHit('ask_stellium', { creditsNeeded: 1, creditsAvailable: 0 });
      setShowPaywall(true);
      return;
    }

    const userMessage = trimmedMessage;
    setInputMessage('');
    setError(null);
    setShowPaywall(false);

    setMessages(prev => [...prev, {
      id: makeMessageId(),
      role: 'user',
      content: userMessage || 'Selected context',
      selectedElements: hasSelection ? selectedElements : null,
      timestamp: new Date().toISOString(),
    }]);

    trackChatMessageSent(contentType);
    setLoading(true);

    let creditsSnapshot = null;
    try {
      if (!isPlus) creditsSnapshot = applyOptimisticCreditSpend(1);

      let response;
      let responseText = '';

      if (contentType === 'birthchart' || contentType === 'analysis') {
        const requestBody = {};
        if (userMessage) {
          requestBody.message = userMessage;
          requestBody.query = userMessage;
        }
        if (hasSelection) {
          requestBody.selectedAspects = selectedElements.map(el => el.payload || el);
        }
        response = await enhancedChatForUserBirthChart(contentId, requestBody);
        responseText = response?.response || response?.answer || '';
      } else if (contentType === 'relationship') {
        const scoredItems = hasSelection ? selectedElements.map(el => el.payload || el) : [];
        response = await enhancedChatForRelationship(contentId, userMessage || '', scoredItems);
        responseText = response?.answer || response?.response || '';
      } else if (contentType === 'horoscope') {
        const requestBody = includeHoroscopeReading ? { period: activePeriod } : {};
        const activeDateValue = getActiveDate?.();
        const activeDate = activeDateValue ? new Date(activeDateValue) : null;
        if (activeDate && !Number.isNaN(activeDate.getTime())) {
          const startDate = new Date(activeDate);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(activeDate);
          endDate.setHours(23, 59, 59, 999);
          requestBody.startDate = startDate.toISOString();
          requestBody.endDate = endDate.toISOString();
        }
        if (userMessage) {
          requestBody.query = userMessage;
        }
        if (hasSelection) {
          requestBody.selectedTransits = selectedElements.map(el => el.payload || el);
        }
        response = await generateCustomHoroscope(contentId, requestBody);
        const payload = response?.horoscope || response;
        responseText = payload?.text || payload?.interpretation || response?.response || '';
      }

      if (responseText) {
        setMessages(prev => [...prev, {
          id: makeMessageId(),
          role: 'assistant',
          content: responseText,
          animate: true,
          timestamp: new Date().toISOString(),
        }]);
        if (!preserveSelectionOnSend) {
          setSelectedElements([]);
          setSelectionError(null);
        }

        // Reconcile optimistic counter against backend state.
        if (stelliumUser?._id) {
          fetchEntitlements(stelliumUser._id);
        }
      } else if (response?.error) {
        throw new Error(response.error);
      } else {
        throw new Error('No response received');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      restoreCredits(creditsSnapshot);

      // Simple pricing surfaces a typed gate (code + options) on both 429 and
      // 402; show the model-aware paywall from it.
      const gateBody = err.details || err.response || null;
      if (isSimple && gateBody?.code) {
        setPaywallGate(gateBody);
        setShowPaywall(true);
      } else if (err.status === 429 || err.message?.includes('LIMIT_REACHED')) {
        setError("You've reached today's Gravity Chat fair-use limit. Try again tomorrow.");
      } else if (err.status === 402 || err.status === 403 || err.message?.includes('403')) {
        setPaywallGate(gateBody);
        setShowPaywall(true);
        setError(err.message || 'Failed to send message');
      } else {
        setError(err.message || 'Failed to send message');
      }
      setMessages(prev => prev.slice(0, -1));
      setInputMessage(userMessage);
    } finally {
      setLoading(false);
    }
  }, [
    inputMessage,
    loading,
    config,
    contentId,
    contentType,
    isPlus,
    hasEnoughCredits,
    applyOptimisticCreditSpend,
    restoreCredits,
    fetchEntitlements,
    stelliumUser?._id,
    selectedElements,
    activePeriod,
    getActiveDate,
    includeHoroscopeReading,
    preserveSelectionOnSend
  ]);

  useEffect(() => {
    if (loading || !externalMessage?.nonce || externalMessageRef.current === externalMessage.nonce) return;
    externalMessageRef.current = externalMessage.nonce;
    handleSendMessage(externalMessage.message);
  }, [externalMessage, handleSendMessage, loading]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestedClick = (question) => {
    setInputMessage(question);
    textareaRef.current?.focus();
  };

  const primarySubjectName = useMemo(() => {
    if (!contextLabel) return 'this chart';
    return contextLabel
      .replace(/^Admin\s+/i, '')
      .replace(/^About\s+/i, '')
      .replace(/\s+(birth chart|chart|horoscope|relationship)$/i, '')
      .trim() || 'this chart';
  }, [contextLabel]);

  const generatedSuggestions = useMemo(() => {
    if (selectedElements.length > 0) {
      const first = selectedElements[0]?.label || selectedElements[0]?.description || 'this placement';
      const second = selectedElements[1]?.label || selectedElements[1]?.description;
      return [
        `How should I understand ${first}?`,
        second ? `How do ${first} and ${second} work together?` : `Where does ${first} show up most strongly?`,
        `What is the growth edge in ${first}?`,
        `How can I work with ${first} in daily life?`,
        `What does ${first} reveal that I might overlook?`
      ];
    }

    if (suggestedQuestions && suggestedQuestions.length > 0) {
      return suggestedQuestions.slice(0, 5);
    }

    if (contentType === 'birthchart' || contentType === 'analysis') {
      const mars = positionsData.find(item => item.planet === 'Mars') || positionsData[0];
      const moon = positionsData.find(item => item.planet === 'Moon') || positionsData[1];
      const sun = positionsData.find(item => item.planet === 'Sun') || positionsData[2];
      return [
        mars ? `What does ${primarySubjectName}'s ${mars.label}${mars.house ? ` in the ${getOrdinal(mars.house)} house` : ''} say about drive?` : `What is the strongest theme in ${primarySubjectName}'s chart?`,
        moon ? `How does ${moon.label} shape emotional needs?` : `What does this chart reveal about emotional patterns?`,
        sun ? `Where does ${sun.label} want to be expressed most clearly?` : `What does this chart say about identity?`,
        'What relationship patterns stand out here?',
        'What should I focus on for personal growth?'
      ];
    }

    return [
      'What should I pay attention to?',
      'What is the strongest pattern here?',
      'Where is the growth edge?',
      'What is the practical takeaway?',
      'What might I be missing?'
    ];
  }, [contentType, positionsData, primarySubjectName, selectedElements, suggestedQuestions]);

  if (!isOpen) return null;

  const canSend = !loading && (inputMessage.trim().length > 0 || selectedElements.length > 0);
  const activeFilter = getActiveFilter();
  const filterOptions = getFilterOptions(contentType);
  const totalCount = selectableElements.length;
  const shownCount = filteredOverlayElements.length;
  const selectionLimitReached = selectedElements.length >= selectionLimit;
  const contextActionLabel = selectedElements.length > 0 ? 'Edit' : 'Add context';
  const chartOwner = /^(your|this)$/i.test(primarySubjectName)
    ? 'your chart'
    : `${primarySubjectName}'s chart`;
  const welcomeTitle = (contentType === 'birthchart' || contentType === 'analysis')
    ? `Ask anything about ${chartOwner}`
    : 'Gravity Chat';
  const panelSubtitle = (contentType === 'birthchart' || contentType === 'analysis')
    ? `Ask anything about ${chartOwner}`
    : (placeholderText || 'Ask questions and get personalized insights.');

  const panelBody = (
      <div
        ref={panelRef}
        className={`ask-panel ${isOpen ? 'ask-panel--open' : ''}${variant === 'dock' ? ' ask-panel--dock' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="ask-panel__header">
          <div className="ask-panel__header-text">
            <div className="ask-panel__title">Gravity Chat</div>
            <div className="ask-panel__context">{panelSubtitle}</div>
          </div>
          <button className="ask-panel__close" onClick={onClose} aria-label="Close panel">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Context picker stays inline for drawers, or portals into the
            dedicated Reader inspector column in full-screen layouts. */}
        {hasContextData && (() => {
          const contextPicker = (
          <div
            className={`ask-panel__compact-context${persistentContext ? ' ask-panel__compact-context--persistent' : ''}`}
            ref={overlayRef}
          >
            <div className="ask-panel__compact-bar">
              <div className="ask-panel__context-summary">
                <button
                  className={`ask-panel__context-trigger ${overlayOpen ? 'ask-panel__context-trigger--open' : ''} ${selectedElements.length > 0 ? 'ask-panel__context-trigger--has-selection' : ''}`}
                  onClick={() => {
                    if (!persistentContext) setOverlayOpen(!overlayOpen);
                  }}
                  aria-expanded={persistentContext || overlayOpen}
                >
                  Context
                </button>
                <div className="ask-panel__summary-chips" aria-live="polite">
                  {selectedElements.length > 0 ? selectedElements.map((element, idx) => (
                    <span key={element.key || idx} className="ask-panel__summary-chip">
                      {element.label || element.description || element.code}
                    </span>
                  )) : (
                    <span className="ask-panel__summary-placeholder">Add up to {selectionLimit} chart elements to focus your question</span>
                  )}
                </div>
              </div>
              <div className="ask-panel__compact-right">
                <span className="ask-panel__selection-count">
                  {selectedElements.length} of {selectionLimit} selected
                </span>
                {selectedElements.length > 0 && (
                  <button className="ask-panel__selection-clear-inline" onClick={clearSelections}>
                    Clear
                  </button>
                )}
                <button className="ask-panel__context-action" onClick={() => setOverlayOpen(!overlayOpen)}>
                  {overlayOpen ? 'Done' : contextActionLabel}
                </button>
              </div>
            </div>

            {selectionError && !overlayOpen && !persistentContext && (
              <div className="ask-panel__compact-error" role="status">
                {selectionError}
              </div>
            )}

            {(overlayOpen || persistentContext) && (
              <div className="ask-panel__items-overlay">
                <div className="ask-panel__overlay-help-row">
                  <div className="ask-panel__overlay-help">
                    Inspect the chart, then explicitly add up to {selectionLimit} details as context.
                  </div>
                  {inspectorFocus && (
                    <button
                      type="button"
                      className="ask-panel__show-all"
                      onClick={clearInspectorFocus}
                    >
                      Show all
                    </button>
                  )}
                </div>
                <div className="ask-panel__overlay-header">
                  <div className="ask-panel__overlay-tabs">
                    {filterOptions.map(option => (
                      <button
                        key={option.value}
                        className={`ask-panel__overlay-tab ${activeFilter === option.value ? 'ask-panel__overlay-tab--active' : ''}`}
                        onClick={() => setActiveFilterCb(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <span className="ask-panel__overlay-count">
                    {overlaySearch.trim() && shownCount !== totalCount
                      ? `${shownCount} of ${totalCount}`
                      : totalCount
                    }
                  </span>
                </div>
                <div className="ask-panel__overlay-search">
                  <input
                    type="text"
                    value={overlaySearch}
                    onChange={(e) => setOverlaySearch(e.target.value)}
                    placeholder="Search..."
                    className="ask-panel__search-input"
                  />
                  {overlaySearch && (
                    <button
                      className="ask-panel__search-clear"
                      onClick={() => setOverlaySearch('')}
                      aria-label="Clear search"
                    >
                      &times;
                    </button>
                  )}
                </div>
                {selectionError && (
                  <div className="ask-panel__overlay-error">{selectionError}</div>
                )}
                {(contentType === 'birthchart' || contentType === 'analysis') && (
                  <div className="ask-panel__table-head" aria-hidden="true">
                    <span>Body</span>
                    <span>Position / aspect</span>
                    <span>House / orb</span>
                    <span>Context</span>
                  </div>
                )}
                <div className="ask-panel__overlay-items-wrap">
                  <div className="ask-panel__overlay-items">
                    {(() => {
                      // Check if relationship items have mixed badge types (for section dividers)
                      const showDividers = contentType === 'relationship' && filteredOverlayElements.length > 1 &&
                        new Set(filteredOverlayElements.map(el => getCategoryBadgeInfo(contentType, el).label)).size > 1;

                      return filteredOverlayElements.map((element, index) => {
                        const badge = getCategoryBadgeInfo(contentType, element);
                        const key = element.key || element.code;
                        const selected = isSelected(key);
                        const disabledByLimit = selectionLimitReached && !selected;
                        const cells = getContextTableCells(contentType, element);
                        const externallyHovered = elementIsExternallyHovered(element);
                        const prevBadge = index > 0 ? getCategoryBadgeInfo(contentType, filteredOverlayElements[index - 1]) : null;
                        const sectionHeader = showDividers && (!prevBadge || prevBadge.label !== badge.label);

                        if (contentType === 'birthchart' || contentType === 'analysis') {
                          return (
                            <div
                              key={key}
                              className={`ask-panel__table-row ${selected ? 'ask-panel__table-row--selected' : ''} ${externallyHovered ? 'ask-panel__table-row--highlighted' : ''}`}
                              onMouseEnter={() => onElementHover?.({ ...element, key })}
                              onMouseLeave={() => onElementHover?.(null)}
                              onFocus={() => onElementHover?.({ ...element, key })}
                              onBlur={(event) => {
                                if (!event.currentTarget.contains(event.relatedTarget)) onElementHover?.(null);
                              }}
                            >
                              <button
                                type="button"
                                className="ask-panel__table-focus"
                                onClick={() => inspectElement({ ...element, key })}
                                aria-label={`Show ${element.label} on the chart`}
                              >
                                <strong>{cells.primary}</strong>
                                <span>{cells.detail}</span>
                                <small>{cells.metric}</small>
                              </button>
                              <button
                                type="button"
                                className={`ask-panel__table-add ${selected ? 'ask-panel__table-add--selected' : ''}`}
                                onClick={() => handleToggleElement({ ...element, key })}
                                disabled={disabledByLimit}
                                aria-label={`${selected ? 'Remove' : 'Add'} ${element.label} ${selected ? 'from' : 'to'} context`}
                              >
                                <span aria-hidden="true">{selected ? '✓' : '+'}</span>
                                {selected ? 'Added' : 'Add'}
                              </button>
                            </div>
                          );
                        }

                        return (
                          <React.Fragment key={key}>
                            {sectionHeader && (
                              <div className="ask-panel__section-divider">{badge.label}</div>
                            )}
                            <button
                              className={`ask-panel__item-row ${selected ? 'ask-panel__item-row--selected' : ''} ${disabledByLimit ? 'ask-panel__item-row--disabled' : ''}`}
                              onClick={() => handleToggleElement({ ...element, key })}
                              disabled={disabledByLimit}
                            >
                              <span className="ask-panel__item-check" aria-hidden="true">
                                {selected ? '✓' : ''}
                              </span>
                              <div className="ask-panel__item-label">
                                {element.label}
                              </div>
                              {contentType === 'horoscope' && element.payload && (
                                <div className="ask-panel__item-sublabel">
                                  {formatTransitDateRange(element.payload)}
                                </div>
                              )}
                              <span className={`ask-panel__item-badge ${badge.colorClass}`}>
                                {badge.label}
                              </span>
                            </button>
                          </React.Fragment>
                        );
                      });
                    })()}
                    {filteredOverlayElements.length === 0 && (
                      <div className="ask-panel__selection-empty">
                        {overlaySearch.trim() ? 'No items match your search.' : 'No items match this filter.'}
                      </div>
                    )}
                  </div>
                  {filteredOverlayElements.length > 6 && (
                    <div className="ask-panel__overlay-fade" />
                  )}
                </div>
              </div>
            )}
          </div>
          );
          if (!persistentContext) return contextPicker;
          return contextContainer ? createPortal(contextPicker, contextContainer) : null;
        })()}

        {/* Messages */}
        <div className="ask-panel__messages">
          {loadingHistory ? (
            <div className="ask-panel__loading">
              <div className="loading-spinner"></div>
              <p>Loading chat history...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="ask-panel__welcome">
              <div className="ask-panel__welcome-icon">&#10024;</div>
              <h3>{welcomeTitle}</h3>
              <p>{placeholderText || 'Ask questions and get personalized insights.'}</p>
              {generatedSuggestions.length > 0 && (
                <div className="ask-panel__suggestions">
                  <p className="ask-panel__suggestions-label">Try asking:</p>
                  {generatedSuggestions.map((q, i) => (
                    <span
                      key={i}
                      className="ask-panel__suggestion"
                      onClick={() => handleSuggestedClick(q)}
                    >
                      {q}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {messages.map((msg, index) => {
                const messageId = msg.id || `idx-${index}`;
                return (
                <div
                  key={messageId}
                  className={`ask-panel__message ${msg.role === 'user' ? 'ask-panel__message--user' : 'ask-panel__message--assistant'}`}
                >
                  {msg.role === 'user' && msg.selectedElements && msg.selectedElements.length > 0 && (
                    <div className="ask-panel__selected-summary">
                      {msg.selectedElements.slice(0, 3).map((element, idx) => (
                        <span key={idx} className="ask-panel__selected-chip">
                          {element.label || element.description || element.code}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="ask-panel__message-content">
                    {msg.role === 'assistant' ? (
                      <StreamingTextReveal
                        text={msg.content}
                        animate={Boolean(msg.animate)}
                        onRevealProgress={scrollToLatestMessage}
                        onRevealComplete={() => markMessageRevealComplete(messageId)}
                      />
                    ) : (
                      msg.content.split('\n').map((paragraph, pIndex) => (
                        paragraph.trim() && <p key={pIndex}>{paragraph}</p>
                      ))
                    )}
                  </div>
                  {msg.role === 'assistant' && !msg.animate && (
                    <div className="ask-panel__msg-actions">
                      <button
                        type="button"
                        className="ask-panel__msg-action"
                        onClick={() => restreamMessage(messageId)}
                        title="Replay the streaming animation"
                      >
                        <span aria-hidden="true">&#10227;</span> Re-stream
                      </button>
                      <button
                        type="button"
                        className="ask-panel__msg-action"
                        onClick={() => hideMessage(messageId)}
                        title="Hide this answer from view"
                      >
                        <span aria-hidden="true">&#10005;</span> Hide
                      </button>
                    </div>
                  )}
                </div>
                );
              })}
              {loading && (
                <div className="ask-panel__message ask-panel__message--assistant">
                  <div className="ask-panel__message-content ask-panel__typing">
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="ask-panel__error">{error}</div>
        )}

        {/* Insufficient Credits Modal */}
        <InsufficientCreditsModal
          isOpen={showPaywall}
          onClose={() => { setShowPaywall(false); setPaywallGate(null); }}
          creditsNeeded={1}
          creditsAvailable={credits.total}
          gate={paywallGate}
          onBuyCredits={() => { setShowPaywall(false); navigate('/pricingTable'); }}
          onSubscribe={() => { setShowPaywall(false); navigate('/pricingTable'); }}
        />

        {/* Input with inline chips */}
        <div className="ask-panel__credit-cost">
          <span aria-hidden="true">✦</span>
          <span>
            {isSimple
              ? (isPlus
                  ? 'Included with Plus · fair use applies'
                  : `${chatQuestionsRemaining} free question${chatQuestionsRemaining === 1 ? '' : 's'} left`)
              : (isPlus ? 'Included with Plus · fair use applies' : `1 credit per message · ${credits.total} remaining`)}
          </span>
        </div>
        <div className="ask-panel__input">
          <div className="ask-panel__input-wrapper">
            {selectedElements.length > 0 && (
              <div className="ask-panel__context-chips">
                {selectedElements.map((el, idx) => (
                  <span key={idx} className="ask-panel__context-chip">
                    {el.label || el.description || el.code}
                    <button
                      className="ask-panel__chip-dismiss"
                      onClick={() => removeSelection(el.key)}
                      aria-label="Remove"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholderText || 'Ask a question...'}
              rows={2}
              disabled={loading}
            />
          </div>
          <button
            className="ask-panel__send"
            onClick={() => handleSendMessage()}
            disabled={!canSend}
          >
            {loading ? '...' : '\u2192'}
          </button>
        </div>
      </div>
  );

  if (variant === 'dock') {
    return isOpen ? panelBody : null;
  }

  return createPortal((
    <div className="ask-panel-backdrop" onClick={onClose}>
      {panelBody}
    </div>
  ), document.body);
}

export default GravityChatPanel;
